/* eslint-disable @typescript-eslint/camelcase */
import Vue from "@/plugins"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { Market, AddMarket } from "@/models"
import {
    GET_MARKET_LIST,
    GET_MARKET_BY_ID,
    GET_JWT_TOKEN,
    HAS_SELECTED_MARKET,
    GET_SELECTED_MARKET_ID,
    GET_SELECTED_MARKET,
    GET_SELECTED_MARKET_TOTAL_VENUE,
    GET_SELECTED_MARKET_TOTAL_BOX,
    GET_SELECTED_MARKET_TOTAL_ONLINE_BOX,
    GET_SELECTED_MARKET_TOTAL_OFFLINE_BOX,
    GET_SELECTED_MARKET_TOTAL_UPDATE_REQUIRED_BOX,
    GET_MARKET_TOTAL_VENUE_FN,
    GET_MARKET_TOTAL_BOX_FN,
    GET_MARKET_TOTAL_ONLINE_BOX_FN,
    GET_MARKET_TOTAL_OFFLINE_BOX_FN,
    GET_MARKET_TOTAL_UPDATE_REQUIRED_BOX_FN,
} from "@/store/types/getters"
import {
    LOAD_MARKET_LIST,
    ADD_NEW_MARKET,
    UPDATE_SELECTED_MARKET,
    CLEAR_MARKET_LIST,
    CLEAR_SELECTED_MARKET,
} from "@/store/types/mutations"
import { fetchMarketList, addNewMarket } from "@/store/types/actions"
import { isEmpty } from "@/utils/validate"
import { isNull } from "lodash"
import { AxiosError, AxiosResponse } from "axios"
import { notifyRequestError } from "@/utils/notify"

export interface State {
    markets: Market[]
    selected: Market | null
}

const namespaced = true

const state: State = {
    markets: [],
    selected: null,
}

const getters: GetterTree<State, any> = {
    [GET_MARKET_LIST](state: State): Market[] {
        return state.markets
    },
    [GET_MARKET_BY_ID](
        state: State
    ): (market_id: string) => Market | undefined {
        return (market_id: string) => {
            return state.markets.find((x: Market) => x.id === market_id)
        }
    },
    [HAS_SELECTED_MARKET](state: State): boolean {
        return !isNull(state.selected)
    },
    [GET_SELECTED_MARKET](state: State): Market | null {
        return state.selected
    },
    [GET_SELECTED_MARKET_ID](state: State): string | null {
        return !isNull(state.selected) ? state.selected.id : null
    },
    [GET_SELECTED_MARKET_TOTAL_VENUE](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_MARKET]
            ? rootGetters[GET_MARKET_TOTAL_VENUE_FN](
                  getters[GET_SELECTED_MARKET_ID]
              )
            : 0
    },
    [GET_SELECTED_MARKET_TOTAL_ONLINE_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_MARKET]
            ? rootGetters[GET_MARKET_TOTAL_ONLINE_BOX_FN](
                  getters[GET_SELECTED_MARKET_ID]
              )
            : 0
    },
    [GET_SELECTED_MARKET_TOTAL_OFFLINE_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_MARKET]
            ? rootGetters[GET_MARKET_TOTAL_OFFLINE_BOX_FN](
                  getters[GET_SELECTED_MARKET_ID]
              )
            : 0
    },
    [GET_SELECTED_MARKET_TOTAL_UPDATE_REQUIRED_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_MARKET]
            ? rootGetters[GET_MARKET_TOTAL_UPDATE_REQUIRED_BOX_FN](
                  getters[GET_SELECTED_MARKET_ID]
              )
            : 0
    },
    [GET_SELECTED_MARKET_TOTAL_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_MARKET]
            ? rootGetters[GET_MARKET_TOTAL_BOX_FN](
                  getters[GET_SELECTED_MARKET_ID]
              )
            : 0
    },
}

const mutations: MutationTree<State> = {
    [LOAD_MARKET_LIST](state: State, paylod: Market[]) {
        state.markets = paylod
    },
    [ADD_NEW_MARKET](state: State, paylod: Market) {
        state.markets.push(paylod)
    },
    [UPDATE_SELECTED_MARKET](state: State, paylod: Market) {
        state.selected = paylod
    },
    [CLEAR_MARKET_LIST](state: State) {
        state.markets = []
    },
    [CLEAR_SELECTED_MARKET](state: State) {
        state.selected = null
    },
}

const actions: ActionTree<State, any> = {
    [fetchMarketList]({ rootGetters, commit }) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        Vue.prototype
            .$api({
                method: "GET",
                url: "/api/v1/markets",
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Market[]>) => {
                commit(LOAD_MARKET_LIST, resp.data)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to fetch markets")
            })
    },
    [addNewMarket]({ rootGetters, commit }, payload: AddMarket) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        Vue.prototype
            .$api({
                method: "POST",
                url: "/api/v1/markets",
                data: {
                    country_iso: payload.country_iso,
                    company: payload.company,
                },
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Market>) => {
                commit(ADD_NEW_MARKET, resp.data)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to add new market")
            })
    },
}

const module: Module<State, any> = {
    namespaced,
    state,
    getters,
    mutations,
    actions,
}

export default module
